/** @typedef {{ el: HTMLElement, bgEl: HTMLElement | null }} StepData */

class ScrollRevealCircle extends HTMLElement {
  /** @type {number} */
  _totalSteps = 0;

  /** @type {number} */
  _currentStep = -99; // -99 = uninitialized, forces first render always

  /** @type {number} */
  _ringRotation = 0;

  /** @type {(() => void) | null} */
  _boundOnScroll = null;

  /** @type {ResizeObserver | null} */
  _resizeObserver = null;

  /** @type {HTMLElement | null} */
  _section = null;

  /** @type {HTMLElement | null} */
  _ring = null;

  /** @type {NodeListOf<HTMLElement> | null} */
  _stateEls = null;

  /** @type {NodeListOf<HTMLElement> | null} */
  _bgEls = null;

  /** @type {NodeListOf<HTMLElement> | null} */
  _dotEls = null;

  connectedCallback() {
    this._section = /** @type {HTMLElement | null} */ (this.closest('.scroll-reveal-circle'));
    if (!this._section) return;

    this._totalSteps = parseInt(this.dataset.total ?? '0', 10);
    this._ring = this.querySelector('.scroll-reveal-circle__ring');
    this._stateEls = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.scroll-reveal-circle__state')
    );
    this._bgEls = /** @type {NodeListOf<HTMLElement>} */ (
      this._section.querySelectorAll('.scroll-reveal-circle__bg')
    );
    this._dotEls = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.scroll-reveal-circle__progress-dot')
    );

    this._setSectionHeight();

    this._boundOnScroll = this._onScroll.bind(this);
    window.addEventListener('scroll', /** @type {EventListener} */ (this._boundOnScroll), { passive: true });

    this._resizeObserver = new ResizeObserver(() => {
      this._setSectionHeight();
      this._onScroll();
    });
    this._resizeObserver.observe(document.documentElement);

    document.addEventListener('shopify:section:load', () => {
      this._setSectionHeight();
      this._onScroll();
    });

    // Initial render
    this._onScroll();
  }

  disconnectedCallback() {
    if (this._boundOnScroll) {
      window.removeEventListener('scroll', this._boundOnScroll);
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  /**
   * Sets the section height to (totalSteps + 1) × 100vh
   * so there is scrollable room for each step transition.
   */
  _setSectionHeight() {
    if (!this._section) return;
    const stepCount = this._totalSteps + 1; // intro + N steps
    this._section.style.setProperty('--src-height', `${stepCount * 100}vh`);
  }

  _onScroll() {
    if (!this._section) return;

    const rect = this._section.getBoundingClientRect();
    const sectionHeight = this._section.offsetHeight;
    const vh = window.innerHeight;

    // scrolled distance from the moment the section top reaches viewport top
    const scrolledIn = -rect.top;

    // Before the section enters the viewport → show intro/section bg
    if (scrolledIn < 0) {
      if (this._currentStep !== -1) {
        this._currentStep = -1;
        this._ringRotation = 0;
        if (this._ring) this._ring.style.transform = 'rotate(0deg)';
        this._updateActiveState(-1);
        this._updateBackground(-1);
        this._updateDots(-1);
      }
      return;
    }

    // After the section has fully scrolled past → keep last step visible
    if (scrolledIn > sectionHeight - vh) {
      const lastStep = this._totalSteps - 1;
      if (this._currentStep !== lastStep) {
        this._currentStep = lastStep;
        this._updateActiveState(lastStep);
        this._updateBackground(lastStep);
        this._updateDots(lastStep);
      }
      return;
    }

    // Total scrollable distance INSIDE the section
    const scrollable = sectionHeight - vh;

    // Normalised progress [0, 1]
    const progress = Math.max(0, Math.min(1, scrolledIn / scrollable));

    // Map progress to step index: intro = -1, steps = 0..N-1
    // Total states = N + 1 (intro + N blocks), so N+1 buckets
    const totalStates = this._totalSteps + 1;
    const rawIndex = progress * totalStates;
    const stepIndex = Math.min(Math.floor(rawIndex), totalStates - 1); // 0 = intro, 1..N = step blocks

    const activeStep = stepIndex - 1; // -1 for intro, 0..N-1 for blocks

    if (activeStep === this._currentStep) return;

    const direction = activeStep > this._currentStep ? 1 : -1;
    this._currentStep = activeStep;

    // Rotate ring: 90° per step, direction depends on scroll direction
    this._ringRotation += direction * 90;
    if (this._ring) {
      this._ring.style.transform = `rotate(${this._ringRotation}deg)`;
    }

    this._updateActiveState(activeStep);
    this._updateBackground(activeStep);
    this._updateDots(activeStep);
  }

  /**
   * @param {number} activeStep — -1 = intro, 0..N-1 = block steps
   */
  _updateActiveState(activeStep) {
    if (!this._stateEls) return;

    this._stateEls.forEach((el) => {
      const step = el.dataset.step;
      const isIntro = step === 'intro';
      const isActive = isIntro ? activeStep === -1 : parseInt(step ?? '-2', 10) === activeStep;

      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  /**
   * @param {number} activeStep — -1 = intro shows section bg, 0..N-1 = block step bgs
   */
  _updateBackground(activeStep) {
    if (!this._bgEls) return;

    this._bgEls.forEach((el) => {
      const step = el.dataset.step;
      let isActive = false;

      if (step === 'section') {
        // Section-level bg: visible only during intro (activeStep === -1)
        // or when a block has no bg image set (JS doesn't know this — CSS fallback handles it)
        isActive = activeStep === -1;
      } else {
        const idx = parseInt(step ?? '-2', 10);
        isActive = idx === activeStep;
      }

      el.classList.toggle('is-active', isActive);
    });
  }

  /**
   * @param {number} activeStep
   */
  _updateDots(activeStep) {
    if (!this._dotEls) return;

    this._dotEls.forEach((el) => {
      const step = parseInt(el.dataset.step ?? '-1', 10);
      el.classList.toggle('is-active', step === activeStep);
    });
  }
}

if (!customElements.get('scroll-reveal-circle')) {
  customElements.define('scroll-reveal-circle', ScrollRevealCircle);
}
